import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);

  const loadCart = useCallback(async () => {
    if (!user) {
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }

    setLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/cart`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCart(data);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load cart when user changes
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Listen for cart updates from other tabs/pages
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'cart_updated') {
        loadCart();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadCart]);

  const addToCart = async (productId, quantity = 1, customDesign = null) => {
    if (!user) return false;

    try {
      const token = await user.getIdToken();
      const body = { product_id: productId, quantity };
      if (customDesign) {
        body.custom_design = customDesign;
      }

      const response = await fetch(`${API_URL}/api/shop/cart/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await loadCart();
        // Notify other tabs
        localStorage.setItem('cart_updated', Date.now().toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  };

  const removeFromCart = async (itemId) => {
    if (!user) return false;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/cart/remove/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await loadCart();
        localStorage.setItem('cart_updated', Date.now().toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    if (!user) return false;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/cart/update/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ quantity })
      });

      if (response.ok) {
        await loadCart();
        localStorage.setItem('cart_updated', Date.now().toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating cart:', error);
      return false;
    }
  };

  const clearCart = async () => {
    if (!user) return false;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/shop/cart/clear`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setCart({ items: [], total: 0, item_count: 0 });
        localStorage.setItem('cart_updated', Date.now().toString());
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  };

  const value = {
    cart,
    loading,
    loadCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    itemCount: cart.item_count,
    total: cart.total
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
